import { CONSENT, isConsentGranted } from "./consent"
import Cookies from "./cookies"
import { sessionStorage } from "./storage"

declare global {
  interface Window {
    // https://documentation.bloomreach.com/data-hub/docs/sdk-web-tracking
    brweb?: {
      identify(customerIds: object, customerProperties?: object): void
      track(
        eventName: string,
        eventProperties?: EventProperties,
        // SDK uses `fetch keepalive` on `pagehide` but we're told by Bloomreach not to rely on
        // that, hence callbacks to track events preceding a redirect.
        successCallback?: () => void,
        errorCallback?: () => void
      ): void
    }
  }
}

type EventProperties = Record<string, unknown>

const ABANDONED_CARTS_PATH = "/bloomreach/abandoned_carts"
const EMAIL_COOKIE = "email_address"
const NAVIGATION_TIMEOUT = 500
const SEARCH_TERM_STORAGE_KEY = "bloomreach_search_term"

const identify = (): void => {
  if (!window.brweb || !isConsentGranted(CONSENT.TARGETING)) {
    return
  }

  const email = Cookies.get(EMAIL_COOKIE)?.trim().toLowerCase()

  if (!email) {
    return
  }

  window.brweb.identify({ email_id: email })
}

const saveSearchTermToStorage = (searchTerm: string): void => {
  sessionStorage?.setItemIfConsent(CONSENT.TARGETING, SEARCH_TERM_STORAGE_KEY, searchTerm)
}

const searchEvent = (eventProperties: EventProperties): void => {
  const searchTerm = sessionStorage?.getItem(SEARCH_TERM_STORAGE_KEY)

  // Presence of `searchTerm` denotes that user search was performed. Its absence (null or undefined)
  // indicates page loaded via non-search means (e.g. category page link from newsletter, venue
  // page click from home page, etc.) An empty string is a real search with no typed input so
  // it's still valid.
  if (typeof searchTerm !== "string") {
    return
  }

  sessionStorage?.removeItem(SEARCH_TERM_STORAGE_KEY)
  track("search", { ...eventProperties, search_term: searchTerm })
}

const track = (
  eventName: string,
  eventProperties?: EventProperties,
  successCallback?: () => void,
  errorCallback?: () => void
): boolean => {
  if (!window.brweb || !isConsentGranted(CONSENT.TARGETING)) {
    return false
  } else {
    window.brweb.track(eventName, eventProperties, successCallback, errorCallback)
    return true // Reflects whether tracking is available, not whether it succeeded or failed.
  }
}

const trackAbandonedCartOnHide = (
  event: Event,
  cartData: EventProperties,
  productList: Array<{ productId: string; quantity: number }>,
  totalPrice: number
): boolean => {
  if (!isConsentGranted(CONSENT.TARGETING)) {
    return false
  }

  // Leaving the page
  const isPageHide = event.type === "pagehide"
  // Tab or app switch
  const isVisibilityHidden =
    event.type === "visibilitychange" && document.visibilityState === "hidden"

  if (isPageHide || isVisibilityHidden) {
    const cartUpdatePayload = {
      ...cartData,
      item_count: productList.length,
      product_ids: productList.map((product) => product.productId),
      product_list: productList.map((product) => ({
        product_id: product.productId,
        quantity: product.quantity
      })),
      total_price: totalPrice
    }

    window.navigator.sendBeacon(
      ABANDONED_CARTS_PATH,
      new Blob([JSON.stringify({ properties: cartUpdatePayload })], { type: "application/json" })
    )
  }

  // Leaving the page fires both `pagehide` then `visibilitychange`, so callers use this to avoid
  // sending beacon twice.
  return isPageHide
}

const trackOnLinkClick = (
  eventName: string,
  eventProperties: EventProperties,
  event: MouseEvent
): void => {
  const { href } = event.currentTarget as HTMLAnchorElement
  const navigate = (): void => window.location.assign(href)

  // If tracking is available, prevent link click so tracking can proceed before unload.
  // Navigation will occur on either success or failure, but also set a timeout in case neither
  // callback fires.
  if (track(eventName, eventProperties, navigate, navigate)) {
    event.preventDefault()
    window.setTimeout(navigate, NAVIGATION_TIMEOUT)
  }
}

const viewItemEvent = (eventProperties: EventProperties): void => {
  track("view_item", eventProperties)
}

const viewItemEventOnLinkClick = (eventProperties: EventProperties, event: MouseEvent): void => {
  trackOnLinkClick("view_item", eventProperties, event)
}

export {
  type EventProperties,
  identify,
  saveSearchTermToStorage,
  searchEvent,
  trackAbandonedCartOnHide,
  viewItemEvent,
  viewItemEventOnLinkClick
}
